import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, Check, TrendingUp } from "lucide-react";

export function FeatureUnavailableModal({ isOpen, onClose, featureDetails }) {
  const {
    current_plan = 'base',
    feature = 'AI Intro Mail',
    required_plan = 'Tier 1 or higher'
  } = featureDetails || {};

  const planBenefits = {
    'AI Intro Mail': [
      'AI-powered personalized email generation',
      'Contextual insights based on lead data',
      'Professional templates and tone matching',
      'Save hours on email outreach'
    ]
  };

  const benefits = planBenefits[feature] || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <Lock className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Feature Upgrade Required</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {current_plan.charAt(0).toUpperCase() + current_plan.slice(1)} Plan
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2">
              <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <p className="font-medium mb-1">{feature} is a premium feature</p>
                <p className="text-purple-700">
                  This feature is available on <strong>{required_plan}</strong> plans.
                  Upgrade your plan to unlock this powerful tool.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-600" />
                What You'll Get
              </h4>
              <ul className="space-y-2">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-600" />
                Upgrade to Unlock
              </h4>
              <div className="space-y-2 mb-3">
                <div className="bg-white rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">Tier 1 Plan</p>
                      <p className="text-sm text-gray-600">25 users • 2,500 leads</p>
                      <p className="text-xs text-green-600 font-medium mt-1">
                        ✓ {feature} included
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">€59</p>
                      <p className="text-xs text-gray-500">/month</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">Tier 2 Plan</p>
                      <p className="text-sm text-gray-600">50 users • 2,500 leads</p>
                      <p className="text-xs text-green-600 font-medium mt-1">
                        ✓ {feature} + more features
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">€89</p>
                      <p className="text-xs text-gray-500">/month</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 sm:flex-initial"
          >
            Maybe Later
          </Button>
          <Button
            variant="default"
            className="flex-1 sm:flex-initial bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            onClick={() => {
              // TODO: Navigate to pricing/upgrade page
              console.log('Navigate to upgrade page');
              onClose();
            }}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            View Plans & Upgrade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
